import type { PropsWithChildren, ReactNode } from "react";

import type { IconName } from "@similarweb/icons";
import { SWReactIcons } from "@similarweb/icons";
import { colorsPalettes } from "@similarweb/styles";
import { IconButton } from "@similarweb/ui-components";
import { Flex, Text } from "@similarweb/ui-components/foundations";

import { StyledManagementCardIconsWrapper } from "./styles";

export interface IManagementCardHeaderProps {
    title: ReactNode;
    subTitle?: ReactNode;
    titleIconName?: IconName;
    titleIconColor?: string;
    isStaticHeight?: boolean;
    headerActionIcon?: IconName;
    onHeaderActionClick?: VoidFunction;
}

const ManagementCardHeader = ({
    title,
    subTitle,
    titleIconName,
    titleIconColor,
    isStaticHeight,
    headerActionIcon = "helpcenter",
    onHeaderActionClick,
    children,
}: PropsWithChildren<IManagementCardHeaderProps>) => {
    return (
        <Flex
            alignItems="center"
            justifyContent="space-between"
            flex={isStaticHeight ? "unset" : "1"}
            maxHeight="64px"
            position="relative"
            data-automation="management-card-header"
        >
            <Flex
                alignItems="center"
                flex="1"
                height="100%"
                gap="8px"
            >
                {titleIconName ? (
                    <StyledManagementCardIconsWrapper>
                        <SWReactIcons
                            iconName={titleIconName}
                            iconColor={titleIconColor}
                            size="xl"
                        />
                    </StyledManagementCardIconsWrapper>
                ) : null}

                <Flex
                    flexDirection="column"
                    gap="4px"
                >
                    <Text
                        fontVariant="h5_2"
                        color={colorsPalettes.carbon["500"]}
                        data-automation="management-card-title"
                    >
                        {title}
                    </Text>
                    {subTitle ? (
                        <Text
                            fontVariant="caption"
                            color={colorsPalettes.carbon["300"]}
                            data-automation="management-card-sub-title"
                        >
                            {subTitle}
                        </Text>
                    ) : null}
                </Flex>
            </Flex>

            <Flex
                alignItems="center"
                gap="8px"
            >
                {children}

                {onHeaderActionClick ? (
                    <IconButton
                        data-automation="management-card-tutor-button"
                        iconName={headerActionIcon}
                        type="flat"
                        onClick={onHeaderActionClick}
                    />
                ) : null}
            </Flex>
        </Flex>
    );
};

export default ManagementCardHeader;
